import React, {useState, useCallback} from "react";
import PropTypes from "prop-types";
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import ChatBubbleTwoToneIcon from '@mui/icons-material/ChatBubbleTwoTone';
import ChatIcon from '@mui/icons-material/Chat';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import ChatTwoToneIcon from '@mui/icons-material/ChatTwoTone';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import AnnouncementTwoToneIcon from '@mui/icons-material/AnnouncementTwoTone';
import CommentIcon from '@mui/icons-material/Comment';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import CommentTwoToneIcon from '@mui/icons-material/CommentTwoTone';
import ModeCommentIcon from '@mui/icons-material/ModeComment';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import ArgoColorInputHook from "./ArgoColorInputHook";
import ArgoTextFieldHook from "./ArgoTextFieldHook";
import ArgoTextFieldSelectHook from "./ArgoTextFieldSelectHook";
import * as portalShared from "../../portal/shared/metadataConstantsAndUtilities"
import * as TEMPLATE from "../../constants/templateConstants";
import ArgoCheckboxHook from "./ArgoCheckboxHook";
import {cloneObj} from "../../portal/shared/metadataConstantsAndUtilities";

const ArgoTextDisplayCustomHook = (props) => {
    const {
        templateType,
        textEntryType,
        initToObj,
        fieldTextObjName,
        fieldName,
        metadataPropsObj,
        useMetadataPropsObjAltFunction,
        setCustomMetadata,
        indent,
        showDisplay,
        showFont,
        showSize,
        showStyle,
        showWeight,
        showColor,
        showOpacity,
        showTitleEntry,
        displayLabel,
        backgroundColorOverride,
        largeHexEntry,
        paddingTop,
        index,
        showQuestionNum,
        showIndent
    } = props;

    // Initialize state
    const getInitialState = () => {

        if (initToObj === null) {
            return portalShared.getCustomTextObj(templateType, textEntryType);
        } else if (fieldName === "unselectedFontFamily") {
            let textObj = cloneObj(portalShared.customTextObject); // Clone
            textObj.font = initToObj.unselectedFontFamily; // For likert Squares and Circles Embedded Font.
            textObj.fontFamily = initToObj.unselectedFontFamily; // For likert Squares and Circles Embedded Font.
            return (textObj);
        } else {
            return portalShared.setCustomTextObject(initToObj);
        }
    };

    const [state, setRawState] = useState(() => getInitialState());

    const setMergedState = useCallback((updates, callback) => {
        setRawState(prevState => {
            const resolvedUpdates = typeof updates === "function" ? updates(prevState) : updates;
            const nextState = {...prevState, ...resolvedUpdates};

            if (callback) {
                setTimeout(() => callback(nextState), 0);
            }

            return nextState;
        });
    }, []);

    const setMetadataValue = useCallback((currentState = state) => {
        let valueObj = portalShared.setCustomTextObject(currentState);

        let mpo = metadataPropsObj;

        let setCustomMetadataFunction = useMetadataPropsObjAltFunction ? mpo.setCustomMetadataFunctionAlt : mpo.setCustomMetadataFunction;

        // Check to see if a set metadata callback was passed as a prop, if not use the callback passed in the mpo (metadata props object).
        if (setCustomMetadata) {
            setCustomMetadata(fieldTextObjName, fieldName, 'default', mpo.metadataTag, valueObj);
        }
        else {
            setMergedState({metadataValue: JSON.stringify(valueObj)}, () => {
                setCustomMetadataFunction(fieldTextObjName, mpo.template, mpo.templateUpdateFunction, mpo.setStateCallbackFunction, fieldName, "custom", mpo.metadataTag, valueObj);
            });
        }
    }, [fieldTextObjName, fieldName, setCustomMetadata, metadataPropsObj, useMetadataPropsObjAltFunction, setMergedState, state]);

    const onTextChange = useCallback((event, pFieldName) => {
        let value = event.target.value;
        let stateObj = {[pFieldName]: value};
        let iconObj = null;

        switch(pFieldName) {
            case "fontSizeEntry":
                if (state.fontSizing === "pixels") {
                    stateObj = {[pFieldName]: value, "fontSize": value+"px"};
                }
                else {
                    stateObj = {[pFieldName]: value, "fontSize": value+"pt"};
                }
                break;
            case "iconSizeEntry":
                iconObj = {...state.iconObj};
                iconObj.size = value;
                stateObj = {iconObj: iconObj};
                break;
            case "iconPaddingEntry":
                iconObj = {...state.iconObj};
                iconObj.padding = value;
                stateObj = {iconObj: iconObj};
                break;
            case "indent":
                break;
            default:
                console.log("ArgoTextDisplayCustom.js  Switch default E01"); // eslint-disable-line
        }
        const newState = {...state, ...stateObj};
        setMergedState(stateObj, () => setMetadataValue(newState));
    }, [state, setMetadataValue, setMergedState]);

    const onCheckBoxChange = useCallback((event, pFieldName) => {
        const newState = {...state, [pFieldName]: event.target.checked};
        setMergedState({[pFieldName]: event.target.checked}, () => setMetadataValue(newState));
    }, [state, setMetadataValue, setMergedState]);

    const onSelectChange = useCallback((event, pFieldName) => {

        let selected = event.target.value;
        let stateObj = {[pFieldName]: selected};

        switch(pFieldName) {
            case "font":
                stateObj = {[pFieldName]: selected, fontFamily: portalShared.getFontFamily(selected)};
                break;
            case "fontSizing":
            case "variant":

                let fontSizing = pFieldName === "fontSizing" ? selected : state.fontSizing;
                let variant = pFieldName === "variant" ? selected : state.variant;

                let fontSizePoints = portalShared.convertFontVariantToPoints(variant);

                stateObj = {fontSizing: fontSizing, variant: variant, "fontSize": fontSizePoints + "pt", "fontSizeEntry": fontSizePoints};
                break;
            default:
                console.log("ArgoTextDisplayCustom.js  Switch default E03"); // eslint-disable-line

        }

        // After setting the state, call setMetadataValue to update the template
        const newState = {...state, ...stateObj};
        setMergedState(stateObj, () => setMetadataValue(newState));
    }, [state, setMetadataValue, setMergedState]);

    const setColor = useCallback((colorObj) => {
        const newState = {...state, ...colorObj};
        setMergedState(colorObj, () => setMetadataValue(newState));
    }, [state, setMetadataValue, setMergedState]);

    const setIconColor = useCallback((colorObj) => {
        let iconObj = {...state.iconObj}; // Shallow copy current saved iconObj

        portalShared.setObj(iconObj, colorObj.colorObj);

        const newState = {...state, iconObj: iconObj};
        setMergedState({iconObj}, () => setMetadataValue(newState));
    }, [state, setMetadataValue, setMergedState]);

    const onChangeDescriptionIcon = useCallback((event) => {
        let iconObj = {...state.iconObj};
        iconObj.icon = event.target.value;
        const newState = {...state, iconObj: iconObj};
        setMergedState({iconObj}, () => setMetadataValue(newState));
    }, [state, setMetadataValue, setMergedState]);

    return (
        <div>
            <div id="ArgoTextDisplayCustom-container-div" style={{
                display: "flex",
                flexDirection: "row",
                width: '99%',
                backgroundColor: backgroundColorOverride,
                paddingTop: paddingTop
            }}>

                {indent ? <div style={{width: "20px"}}></div> : ""}

                {showDisplay ?
                    <div style={{display: "flex"}}>
                        <ArgoTextFieldSelectHook
                            fileName="ArgoTextDisplayCustom"
                            fieldName="textAlign"
                            label={displayLabel}
                            value={state.textAlign}
                            onChange={onSelectChange}
                            width="160px"
                            menuItems={portalShared.textAlignList}
                            />
                            <div style={{width: "20px"}}></div>
                        </div>
                        : ""
                    }

                    {showTitleEntry && !initToObj.title.includes("pageNum") && !initToObj.title.includes("pageofNum") ?
                        <div style={{display: "flex"}}>
                            <ArgoTextFieldHook
                                fileName="ArgoTextDisplayCustom"
                                fieldName="title"
                                label="Title"
                                value={initToObj.title}
                                onChange={onTextChange}
                                width="140px"
                            />
                            <div style={{width: "20px"}}></div>
                        </div>
                        : ""
                    }

                    {showTitleEntry && (initToObj.title.includes("pageNum") || initToObj.title.includes("pageofNum")) ?
                        <div style={{
                            color: "#01579B",
                            display: "flex",
                            width: "166px",
                            paddingTop: "20px"
                        }}>{initToObj.title.includes("pageNum") ? "Page X" : "Page X of Y"}</div> : ""
                    }

                    {showFont ?
                        <ArgoTextFieldSelectHook
                            fileName="ArgoTextDisplayCustom"
                            fieldName="font"
                            label="Font"
                            value={state.font}
                            onChange={onSelectChange}
                            width="140px"
                            menuItems={portalShared.fontsList}
                        /> : ""
                    }

                    <div style={{width: "20px"}}></div>

                    {showSize ?
                        <div style={{display: "flex"}}>
                            <ArgoTextFieldSelectHook
                                fileName="ArgoTextDisplayCustom"
                                fieldName="fontSizing"
                                label="Sizing"
                                value={state.fontSizing}
                                onChange={onSelectChange}
                                width="110px"
                                menuItems={portalShared.sizingTypeList}
                            />

                            <div style={{width: "20px"}}></div>

                            <div>
                                {
                                    (state.fontSizing === 'variant') ?
                                        <ArgoTextFieldSelectHook
                                            fileName="ArgoTextDisplayCustom"
                                            fieldName="variant"
                                            label="Variant"
                                            value={state.variant}
                                            onChange={onSelectChange}
                                            width="110px"
                                            menuItems={portalShared.variantTypesList}
                                        />
                                        :
                                        <ArgoTextFieldHook
                                            fileName="ArgoTextDisplayCustom"
                                            fieldName="fontSizeEntry"
                                            label={state.fontSizing === 'points' ? "Points" : "Pixels"}
                                            value={(typeof state.fontSizeEntry === "string") ? parseInt(state.fontSizeEntry) : state.fontSizeEntry}
                                            type="number"
                                            onChange={onTextChange}
                                            width="70px"
                                        />
                                }
                            </div>

                            <div style={{width: "20px"}}></div>
                        </div> : ""
                    }

                    {showStyle ?
                        <ArgoTextFieldSelectHook
                            fileName="ArgoTextDisplayCustom"
                            fieldName="fontStyle"
                            label="Style"
                            value={state.fontStyle}
                            onChange={onSelectChange}
                            width="110px"
                            menuItems={portalShared.fontStyleList}
                        /> : ""
                    }

                    <div style={{width: "20px"}}></div>

                    {showWeight ?
                        <ArgoTextFieldSelectHook
                            fileName="ArgoTextDisplayCustom"
                            fieldName="fontWeight"
                            label="Weight"
                            value={state.fontWeight}
                            onChange={onSelectChange}
                            width="110px"
                            menuItems={portalShared.fontWeightList}
                        /> : ""
                    }

                    <div style={{width: "20px"}}></div>

                    {showColor ?
                        <ArgoColorInputHook opacityFlag={showOpacity}
                                        setColor={setColor}
                                        initToObj={initToObj}
                                        largeHexEntry={largeHexEntry}
                                        index={index}/> : ""
                    }

                    <div style={{width: "20px"}}></div>

                    {showIndent ?
                        <ArgoTextFieldHook
                            fileName="ArgoTextDisplayCustom"
                            fieldName="indent"
                            label="Indent (px)"
                            value={parseInt(state.indent)}
                            type="number"
                            onChange={onTextChange}
                            width="140px"
                        /> : ""
                    }

                    <div style={{width: "20px"}}></div>

                    {showQuestionNum ?
                        <ArgoCheckboxHook
                            fileName="ArgoTextDisplayCustom"
                            fieldName="showQuestionNum"
                            label="Show Question Number"
                            checked={state.showQuestionNum}
                            onChange={onCheckBoxChange}
                            width="300px"
                            paddingTop="16px"
                        /> : ""
                    }

                </div>
                {textEntryType === "description" ||
                 textEntryType === TEMPLATE.TEXT_TYPE.QUESTIONNAIRE.START_PAGE_SCRIPT ||
                 textEntryType === TEMPLATE.TEXT_TYPE.QUESTIONNAIRE.CLOSE_PAGE_SCRIPT ?
                    <div id="ArgoTextDisplayCustom-container-div" style={{
                        display: "flex",
                        flexDirection: "row",
                        width: '99%',
                        backgroundColor: backgroundColorOverride,
                        paddingTop: paddingTop,
                        paddingBottom: "10px"
                    }}>

                        <div style={{width: "20px"}}></div>

                        <TextField
                            id="descriptionIconSelect"
                            label="Icon"
                            select
                            value={state?.iconObj?.icon}
                            style={{width: "100px"}}
                            onChange={onChangeDescriptionIcon}
                            variant="standard"
                        >
                            <MenuItem value="none">
                                <div>None</div>
                            </MenuItem>
                            <MenuItem value="InfoIcon"><InfoIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="InfoOutlinedIcon"><InfoOutlinedIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="InfoTwoToneIcon"><InfoTwoToneIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="ChatBubbleIcon"><ChatBubbleIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="ChatBubbleOutlineOutlinedIcon"><ChatBubbleOutlineOutlinedIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="ChatBubbleTwoToneIcon"><ChatBubbleTwoToneIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="ChatIcon"><ChatIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="ChatOutlinedIcon"><ChatOutlinedIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="ChatTwoToneIcon"><ChatTwoToneIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="AnnouncementIcon"><AnnouncementIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="AnnouncementOutlinedIcon"><AnnouncementOutlinedIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="AnnouncementTwoToneIcon"><AnnouncementTwoToneIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="CommentIcon"><CommentIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="CommentOutlinedIcon"><CommentOutlinedIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="CommentTwoToneIcon"><CommentTwoToneIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="ModeCommentIcon"><ModeCommentIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="FiberManualRecordIcon"><FiberManualRecordIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="PanoramaFishEyeIcon"><PanoramaFishEyeIcon sx={{width: 16, height: 16}}/></MenuItem>
                            <MenuItem value="HorizontalRuleIcon"><HorizontalRuleIcon sx={{width: 16, height: 16}}/></MenuItem>
                        </TextField>

                        <div style={{width: "20px"}}></div>

                        {state?.iconObj?.icon !== "none" ?
                            <div id="ArgoTextDisplayCustom-icon-container-div" style={{
                                display: "flex",
                                flexDirection: "row",
                                width: '99%',
                                backgroundColor: backgroundColorOverride,
                            }}>
                                <ArgoTextFieldHook
                                    fileName="ArgoTextDisplayCustom"
                                    fieldName="iconSizeEntry"
                                    label={"Size"}
                                    value={state?.iconObj?.size}
                                    type="number"
                                    onChange={onTextChange}
                                    width="100px"
                                />

                                <div style={{width: "20px"}}></div>

                                <ArgoTextFieldHook
                                    fileName="ArgoTextDisplayCustom"
                                    fieldName="iconPaddingEntry"
                                    label={"Padding"}
                                    value={state?.iconObj?.padding}
                                    type="number"
                                    onChange={onTextChange}
                                    width="100px"
                                />

                                <div style={{width: "20px"}}></div>

                                <ArgoColorInputHook setColor={setIconColor}
                                                initToObj={initToObj.iconObj}
                                                largeHexEntry={largeHexEntry}
                                                index={index}
                                />
                        </div> : ""
                    }


                </div> : ""
            }
        </div>
    );
};

ArgoTextDisplayCustomHook.defaultProps = {
    indent: true,
    showDisplay: true,
    showFont: true,
    showSize: true,
    showStyle: true,
    showWeight: true,
    showColor: true,
    showOpacity: true,
    showTitleEntry: false,
    displayLabel: "Display",
    backgroundColorOverride: portalShared.COLOR_LIGHT_GRAY_HEX,
    largeHexEntry: true,
    paddingTop: "10px",
    useMetadataPropsObjAltFunction: false,
    index: 0,
    setCustomMetadata: undefined,
    showQuestionNum: false,
    showIndent: false
};

ArgoTextDisplayCustomHook.propTypes = {
    templateType:  PropTypes.string,
    textEntryType:  PropTypes.string,
    indent: PropTypes.bool,
    showDisplay: PropTypes.bool,
    showFont: PropTypes.bool,
    showSize: PropTypes.bool,
    showStyle: PropTypes.bool,
    showWeight: PropTypes.bool,
    showColor: PropTypes.bool,
    showOpacity: PropTypes.bool,
    showTitleEntry: PropTypes.bool,
    fieldName: PropTypes.string,
    fieldTextObjName: PropTypes.string,
    initToObj: PropTypes.object,
    displayLabel: PropTypes.string,
    backgroundColorOverride: PropTypes.string,
    largeHexEntry: PropTypes.bool,
    paddingTop: PropTypes.string,
    metadataPropsObj: PropTypes.object,
    useMetadataPropsObjAltFunction: PropTypes.bool,
    index: PropTypes.number,
    setCustomMetadata: PropTypes.func,
    showQuestionNum: PropTypes.bool,
    showIndent: PropTypes.bool
};

export default ArgoTextDisplayCustomHook;

